// src/pages/BoardList.js
import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import CenterLayout from '../components/CenterLayout';

// 이모지 아바타 공통 배열
const emojiList = ['😀'];
const PAGE_SIZE = 10;

export default function BoardList({ user, userData }) {
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('latest');
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const nav = useNavigate();

  // 모바일 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 실시간 구독: posts 컬렉션
  useEffect(() => {
    const q = query(
      collection(db, 'posts'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap =>
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  // 삭제 핸들러
  const handleDelete = async id => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'posts', id));
  };

  // 검색/정렬/페이지네이션 적용
  const filtered = posts.filter(post => post.title.toLowerCase().includes(search.toLowerCase()));
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'latest') return b.createdAt?.toDate() - a.createdAt?.toDate();
    if (sortBy === 'oldest') return a.createdAt?.toDate() - b.createdAt?.toDate();
    if (sortBy === 'title') return a.title.localeCompare(b.title);
    return 0;
  });
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const paged = sorted.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  // 이모지 아바타 (기본 스마일 아이콘)
  const getEmoji = (authorId, isAnonymous) => {
    if (isAnonymous) return '🕵️';
    if (!authorId) return emojiList[0];
    if (authorId === user.id) return emojiList[userData?.iconIdx ?? 0] || emojiList[0];
    return emojiList[Number(authorId.slice(-1)) % emojiList.length];
  };

  return (
    <CenterLayout>
      {/* 게시판 헤더 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        position: 'relative',
        background: 'white',
        padding: isMobile ? '1.2rem 1.5rem' : '1.5rem 2rem',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
        border: '1px solid #f4f4f8'
      }}>
        <div style={{
          position: 'absolute',
          left: '0',
          top: '0',
          width: '6px',
          height: '100%',
          background: '#4f46e5'
        }}></div>
        <h2 style={{
          margin: 0,
          marginLeft: '6px',
          fontSize: isMobile ? '1.3rem' : '1.5rem',
          fontWeight: 600,
          color: '#111827',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? '36px' : '42px',
            height: isMobile ? '36px' : '42px',
            borderRadius: '12px',
            background: '#f3f4ff',
            color: '#4f46e5'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          게시판
        </h2>
        <button 
          onClick={() => nav('/board/create')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.5rem',
            background: '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '0.95rem',
            cursor: 'pointer',
            boxShadow: '0 2px 5px rgba(79, 70, 229, 0.15)',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(79, 70, 229, 0.2)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 5px rgba(79, 70, 229, 0.15)';
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          {isMobile ? '글쓰기' : '새 글 작성'}
        </button>
      </div>

      {/* 검색/정렬 영역 */}
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        padding: '1rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        border: '1px solid #f1f5f9',
      }}>
        <div style={{
          display: 'flex',
          gap: '1rem',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          alignItems: 'center',
        }}>
          <div style={{
            flex: isMobile ? '1 0 100%' : 1,
            position: 'relative',
          }}>
            <input
              placeholder="제목으로 검색"
              value={search}
              onChange={e=>{setSearch(e.target.value); setPage(1);}}
              style={{
                width: '100%',
                padding: '0.75rem',
                paddingLeft: '2.5rem',
                borderRadius: '0.5rem',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                outline: 'none',
              }}
            />
            <svg 
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
              }}
              xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
          <select
            value={sortBy}
            onChange={e=>{setSortBy(e.target.value); setPage(1);}}
            style={{
              padding: '0.75rem',
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              fontSize: '0.95rem',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              backgroundSize: '1rem',
              paddingRight: '2.5rem',
              minWidth: '120px',
            }}
          >
            <option value="latest">최신순</option>
            <option value="oldest">오래된순</option>
            <option value="title">제목순</option>
          </select>
        </div>
      </div>

      {/* 게시글 목록 */}
      <div style={{
        marginBottom: isMobile ? '5rem' : '2rem',
      }}>
        {paged.length === 0 ? (
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '3rem 1.5rem',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            border: '1px solid #f1f5f9',
          }}>
            <div style={{
              marginBottom: '1rem',
              color: '#94a3b8',
              display: 'flex',
              justifyContent: 'center',
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v1m2 13a2 2 0 0 1-2-2V7m2 13a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
              </svg>
            </div>
            <p style={{
              margin: '0',
              fontSize: '1.1rem',
              fontWeight: 500,
              color: '#64748b',
              marginBottom: '1.5rem',
            }}>검색 결과가 없습니다.</p>
            <button 
              onClick={() => nav('/board/create')}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              첫 글 작성하기
            </button>
          </div>
        ) : (
          <>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}>
              {paged.map(post => (
                <div
                  key={post.id}
                  style={{
                    background: 'white',
                    borderRadius: '0.75rem',
                    padding: '1.25rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid #f1f5f9',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    position: 'relative',
                  }}
                  onClick={() => nav(`/board/${post.id}`)}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '0.75rem',
                    gap: '0.75rem',
                  }}>
                    <div style={{
                      fontSize: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '2.5rem',
                      height: '2.5rem',
                      background: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}>
                      {getEmoji(post.authorId, post.isAnonymous)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        margin: '0 0 0.5rem 0',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        color: '#1e293b',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {post.title}
                      </h3>
                      <div style={{
                        display: 'flex',
                        gap: '0.75rem',
                        alignItems: 'center',
                        color: '#64748b',
                        fontSize: '0.85rem',
                      }}>
                        <span style={{ fontWeight: 500 }}>{post.authorName}</span>
                        <span style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.25rem' 
                        }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          {new Date(post.createdAt?.toDate()).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p style={{
                    margin: '0 0 1rem 0',
                    fontSize: '0.95rem',
                    color: '#475569',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.5,
                  }}>
                    {post.content.slice(0, 150) + (post.content.length > 150 ? '...' : '')}
                  </p>
                  {/* 삭제 버튼 */}
                  {post.authorId === user.id && (
                    <button 
                      onClick={e => {
                        e.stopPropagation();
                        handleDelete(post.id);
                      }}
                      style={{
                        position: 'absolute',
                        top: '1.25rem',
                        right: '1.25rem',
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        border: 'none',
                        borderRadius: '0.375rem',
                        padding: '0.375rem 0.5rem',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                      삭제
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '0.5rem',
                marginTop: '2rem',
                flexWrap: 'wrap'
              }}>
                {Array.from({length: totalPages}, (_,i) => i+1).map(num => (
                  <button
                    key={num}
                    onClick={() => setPage(num)}
                    disabled={num === page}
                    style={{
                      width: '2.5rem',
                      height: '2.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0.5rem',
                      background: num === page ? '#6366f1' : 'white',
                      color: num === page ? 'white' : '#64748b',
                      border: num === page ? 'none' : '1px solid #e2e8f0',
                      fontWeight: num === page ? 600 : 400,
                      cursor: num === page ? 'default' : 'pointer',
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </CenterLayout>
  );
}
